import React from 'react';
import { Spin, Alert, Button, Space, Typography } from 'antd';
import {
    FastBackwardOutlined,
    FastForwardOutlined,
    PauseCircleOutlined,
    PlayCircleOutlined,
} from '@ant-design/icons';
import { getVideoPlayUrl } from '../../api/video';

const SKIP_SECONDS = 10;

/** 直连后端 Range 流式播放（不整文件下载到内存） */
class VideoPlayer extends React.Component {
    constructor(props) {
        super(props);
        this.videoRef = React.createRef();
        this.state = {
            src: '',
            loading: true,
            error: '',
            playing: false,
            currentTime: 0,
            duration: 0,
            seeking: false,
        };
    }

    componentDidMount() {
        this.initSrc(this.props.videoId);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.videoId !== this.props.videoId) {
            this.initSrc(this.props.videoId);
        }
    }

    initSrc = (videoId) => {
        if (!videoId) {
            return;
        }
        this.setState({
            loading: true,
            error: '',
            src: getVideoPlayUrl(videoId),
        });
    };

    handleCanPlay = () => {
        this.setState({ loading: false });
    };

    handleError = () => {
        const el = this.videoRef.current;
        const code = el?.error?.code;
        const detail =
            code === 2
                ? '网络错误或视频地址无效'
                : code === 4
                  ? '视频格式不支持或文件已损坏'
                  : '请确认已登录且有观看权限';
        this.setState({
            loading: false,
            error: `视频加载失败：${detail}`,
        });
    };

    seekBy = (delta) => {
        const el = this.videoRef.current;
        if (!el || !Number.isFinite(el.duration)) return;
        const next = Math.min(Math.max(0, el.currentTime + delta), el.duration);
        el.currentTime = next;
        this.setState({ currentTime: next });
    };

    togglePlay = () => {
        const el = this.videoRef.current;
        if (!el) return;
        if (el.paused) {
            el.play().catch(() => {});
        } else {
            el.pause();
        }
    };

    formatTime = (sec) => {
        if (!Number.isFinite(sec) || sec < 0) return '00:00';
        const m = Math.floor(sec / 60);
        const s = Math.floor(sec % 60);
        return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    };

    render() {
        const { standalone } = this.props;
        const { src, loading, error, playing, currentTime, duration, seeking } = this.state;

        if (error) {
            return <Alert type="error" message={error} showIcon />;
        }

        return (
            <div className={`video-player${standalone ? ' video-player--standalone' : ''}`}>
                {(loading || seeking) && (
                    <div className="video-player video-player--loading video-player__overlay">
                        <Spin tip={seeking ? '正在跳转...' : '正在缓冲视频...'} />
                    </div>
                )}
                {src && (
                    <video
                        ref={this.videoRef}
                        className="video-player__element"
                        controls
                        autoPlay
                        preload="metadata"
                        src={src}
                        onCanPlay={() => {
                            this.handleCanPlay();
                            this.setState({ seeking: false });
                        }}
                        onLoadedData={this.handleCanPlay}
                        onWaiting={() => this.setState({ seeking: true })}
                        onSeeking={() => this.setState({ seeking: true })}
                        onSeeked={() => this.setState({ seeking: false })}
                        onError={this.handleError}
                        onPlay={() => this.setState({ playing: true })}
                        onPause={() => this.setState({ playing: false })}
                        onTimeUpdate={(e) => this.setState({ currentTime: e.target.currentTime })}
                        onLoadedMetadata={(e) => {
                            this.setState({
                                duration: e.target.duration,
                                loading: false,
                            });
                        }}
                    >
                        您的浏览器不支持视频播放
                    </video>
                )}
                <div className="video-player__toolbar">
                    <Space wrap size="middle" align="center">
                        <Button
                            icon={<FastBackwardOutlined />}
                            onClick={() => this.seekBy(-SKIP_SECONDS)}
                            disabled={loading}
                        >
                            后退 {SKIP_SECONDS} 秒
                        </Button>
                        <Button
                            type="primary"
                            icon={playing ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                            onClick={this.togglePlay}
                            disabled={loading}
                        >
                            {playing ? '暂停' : '播放'}
                        </Button>
                        <Button
                            icon={<FastForwardOutlined />}
                            onClick={() => this.seekBy(SKIP_SECONDS)}
                            disabled={loading}
                        >
                            快进 {SKIP_SECONDS} 秒
                        </Button>
                        <Typography.Text type="secondary" className="video-player__time">
                            {this.formatTime(currentTime)} / {this.formatTime(duration)}
                        </Typography.Text>
                    </Space>
                </div>
            </div>
        );
    }
}

export default VideoPlayer;
